import { Component, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { sdPlanPanelExpand } from './servidores-dedicados.animations';
import { RackeoLocaleService } from '../shared/services/locale.service';
import { MetaService } from '@ngx-meta/core';
import { CanonicalService } from '../shared/services/canonical.service';
import { RACKEO_SITE } from '../shared/rackeo-site';
import { RackeoCartService } from '../shared/services/cart.service';
import type {
  RackeoContent,
  RackeoServidorDedicadoPlanSpec,
  RackeoServidoresDedicadosData,
  RackeoSdSupportCell,
  RackeoSdSupportRow,
  RackeoSdTextBlock,
  RackeoVirtualizacionAddOn
} from '../shared/services/locale.service';

const IMG = 'assets/images/rackeo/servidores-dedicados';

const SD_HERO_DEFAULT_SRC = `${IMG}/hero/banner-servidores-dedicados.png`;
const SD_HERO_DEFAULT_ALT =
  'Personaliza tu servidor dedicado como quieras — Rackeo';

/** PIDs carrito WHMCS — https://panel.rackeo.host/cart.php?a=add&pid=… (SD Plan 1–4). */
const SD_PLAN_PIDS = [31, 32, 34, 35];

const PLAN_SPECS: RackeoServidorDedicadoPlanSpec[] = [
  {
    id: 'sd1',
    pid: SD_PLAN_PIDS[0],
    name: 'SD Plan 1',
    model: 'DELL 1950',
    cpuLine: '2 x Xeon Dual Core 2,66 GHz',
    ramLabel: '4 GB',
    diskLabel: '2x500 GB',
    raidLabel: 'RAID 1',
    tarjetaLabel: '500 GB',
    traficoLabel: '500 GB',
    disponibleLabel: '0',
    bullets: [
      'Incluye 2 procesadores Xeon Dual Core',
      'Xeon 5150 2,66 GHz',
      'Memoria RAM DDRII o III Fully Buffered',
      'Discos SATA 2 o 3',
      "Incluye 8 IP's Públicas",
      '500GB de Tráfico Mensual',
      'Soporte Nivel 1',
      'Tarjeta Ethernet 10/100/1000',
      `Data Center en ${RACKEO_SITE.countryName} Tipo Tier II`
    ],
    footerNote: 'Servidor Dell Power Edge 1950.'
  },
  {
    id: 'sd2',
    pid: SD_PLAN_PIDS[1],
    name: 'SD Plan 2',
    model: 'DELL 1950',
    cpuLine: '2 x Xeon Dual Core 3.0 GHz',
    ramLabel: '8 GB',
    diskLabel: '2x500 GB',
    raidLabel: 'RAID 1',
    tarjetaLabel: '500 GB',
    traficoLabel: '500 GB',
    disponibleLabel: '0',
    bullets: [
      'Incluye 2 procesadores Xeon Dual Core',
      'Xeon 5160 3,00 GHz',
      'Memoria RAM DDRII o III Fully Buffered',
      'Discos SATA 2 o 3',
      "Incluye 8 IP's Públicas",
      '500GB de Tráfico Mensual',
      'Soporte Nivel 1',
      'Tarjeta Ethernet 10/100/1000',
      `Data Center en ${RACKEO_SITE.countryName} Tipo Tier II`
    ],
    footerNote: 'Servidor Dell Power Edge 1950.'
  },
  {
    id: 'sd3',
    pid: SD_PLAN_PIDS[2],
    name: 'SD Plan 3',
    model: 'DELL 2950',
    cpuLine: '2 x Xeon Dual Core 3,0 GHz',
    ramLabel: '4 GB',
    diskLabel: '4x500GB',
    raidLabel: 'RAID 10',
    tarjetaLabel: '500 GB',
    traficoLabel: '500 GB',
    disponibleLabel: '0',
    bullets: [
      'Incluye 2 procesadores Xeon Dual Core',
      'Xeon 5160 3,0 GHz',
      'Memoria RAM DDRII o III Fully Buffered',
      'Discos SATA 2 o 3',
      "Incluye 8 IP's Públicas",
      '500GB de Tráfico Mensual',
      'Soporte Nivel 1',
      'Tarjeta Ethernet 10/100/1000',
      `Data Center en ${RACKEO_SITE.countryName} Tipo Tier II`
    ],
    footerNote: 'Servidor Dell Power Edge 2950.'
  },
  {
    id: 'sd4',
    pid: SD_PLAN_PIDS[3],
    name: 'SD Plan 4',
    model: 'DELL 2950',
    cpuLine: '2 x Xeon Dual Core 3.0 GHz',
    ramLabel: '8 GB',
    diskLabel: '4x500 GB',
    raidLabel: 'RAID 10',
    tarjetaLabel: '500 GB',
    traficoLabel: '500 GB',
    disponibleLabel: '0',
    bullets: [
      'Incluye 2 procesadores Xeon Dual Core',
      'Xeon 5160 3,0 GHz',
      'Memoria RAM DDRII o III Fully Buffered',
      'Discos SATA 2 o 3',
      "Incluye 8 IP's Públicas",
      '500GB de Tráfico Mensual',
      'Soporte Nivel 1',
      'Tarjeta Ethernet 10/100/1000',
      `Data Center en ${RACKEO_SITE.countryName} Tipo Tier II`
    ],
    footerNote: 'Servidor Dell Power Edge 2950.'
  }
];

const SUPPORT_ROWS: RackeoSdSupportRow[] = [
  {
    feature: 'Servicio de Soporte Hardware y Conectividad',
    helpText: 'Soporte de Hardware y Conectividad con tiempo de respuesta de 5 a 30 minutos vía ticket, chat, y/o teléfono',
    cells: ['tick', 'tick', 'tick', 'tick']
  },
  {
    feature: 'Servicio de Soporte Software',
    helpText: 'Soporte de Software con tiempo de respuesta de 4 a 12 hrs vía ticket, chat, y/o teléfono',
    cells: ['15 MIN x mes', '60 MIN x mes', '90 MIN x mes', '120 MIN x mes']
  },
  {
    feature: 'Reemplazo de Hardware en 4 Horas',
    helpText: 'Reemplazo y cambio de hardware en 4 horas',
    cells: ['tick', 'tick', 'tick', 'tick']
  },
  {
    feature: '24/7 Soporte emergencias',
    helpText: 'Soporte vía ticket, vía chat y vía telefónica. Incluye solo soporte de hardware y conectividad del servidor',
    cells: ['tick', 'tick', 'tick', 'tick']
  },
  {
    feature: 'Monitoreo Básico',
    helpText: 'Monitoreo Básico de la conectividad del Servidor a través de Ping.',
    cells: ['tick', 'tick', 'tick', 'tick']
  },
  {
    feature: 'Monitoreo de Servidores con Notificación vía Ticket',
    helpText: 'Monitoreo Básico de la conectividad del Servidor a través de Ping con Notificación vía Correo Electrónico.',
    cells: ['cross', 'tick', 'tick', 'tick']
  },
  {
    feature: 'Monitoreo Hardware',
    helpText: 'Monitoreo de Hardware con Notificación vía Correo Electrónico. Monitoreo de CPU, RAM, Discos Duros, y Fuentes de Poder',
    cells: ['cross', 'cross', 'tick', 'tick']
  },
  {
    feature: 'OS Actualizaciones de Seguridad',
    helpText: 'Actualización y Auditoría de Sistema Operativo',
    cells: ['cross', 'cross', 'cross', 'tick']
  },
  {
    feature: 'Vulnerabilidades Actualizaciones de Seguridad',
    cells: ['cross', 'cross', 'cross', 'tick']
  },
  {
    feature: 'Respuesta Rápida',
    helpText: 'Respuesta a tus tickets, chat y/o telefónica en 15 minutos',
    cells: ['cross', 'cross', 'cross', 'tick']
  },
  {
    feature: 'Auditoría de Seguridad',
    helpText: 'Auditoría de Seguridad de Software. Sujeto a variabilidad de Sistema Operativo y Software',
    cells: ['cross', 'cross', 'cross', '1 X AÑO']
  },
  {
    feature: 'Software Firewall Management (Linux)',
    helpText: 'Instalación y configuración de software de Firewall. Sujeto a compatibilidad de Sistema Operativo y Recursos',
    cells: ['cross', 'cross', 'cross', 'tick']
  }
];

const GENERALES_BLOCKS: RackeoSdTextBlock[] = [
  {
    title: 'REEMPLAZO DE HARDWARE',
    paragraphs: [
      'Te entregamos la más alta tecnología y calidad en nuestros servidores, pero la mejor tecnología puede tener problemas. Con nosotros TUS PROBLEMAS TENDRÁN SOLUCIÓN: si el servidor necesita cambio de una pieza, nuestro equipo lo reemplaza dentro de 4 horas; te lo garantizamos.'
    ]
  },
  {
    title: 'PERSONALIZA TU SERVIDOR',
    image: `${IMG}/generales/tools.png`,
    imageAlt: 'Personalizar servidor',
    paragraphs: [
      'En Rackeo puedes elegir el servidor que mejor se adapte a tus necesidades y podrás personalizarlo como tú desees.'
    ]
  },
  {
    title: 'SOFTWARE',
    image: `${IMG}/generales/disk.png`,
    imageAlt: 'Software',
    paragraphs: [
      'Contamos con software de libre acceso y que le garantiza el óptimo funcionamiento de su servidor sin necesidad de pagar un alto precio en licencias.'
    ]
  },
  {
    title: 'REINICIO A DISTANCIA',
    image: `${IMG}/generales/power_button.png`,
    imageAlt: 'Reinicio a distancia',
    paragraphs: [
      'Si tu servidor dedicado no responde y necesitas reiniciarlo, no es necesario crear un ticket de soporte ni nada engorroso: solo debes utilizar la consola de reinicio a distancia.'
    ]
  }
];

const CARACTERISTICAS_BLOCKS: RackeoSdTextBlock[] = [
  {
    title: 'INSTALACIÓN INMEDIATA',
    image: `${IMG}/caracteristicas/building.png`,
    imageAlt: 'Instalación',
    paragraphs: [
      'No perderás tiempo ni dinero: si solicitas tu servidor dedicado con nosotros y confirmas tu pago correctamente, nuestro equipo se pondrá a trabajar de forma inmediata para que tengas tu servidor funcionando en no más de 24 a 48 horas.'
    ]
  },
  {
    title: 'VENTAJAS DE UN SERVIDOR DEDICADO HÍBRIDO',
    image: `${IMG}/caracteristicas/cp.png`,
    imageAlt: 'Ventajas servidor dedicado',
    paragraphs: [
      'Las ventajas de contratar un Servidor Dedicado Híbrido son múltiples: capacidad de recursos y tecnología, flexibilidad para configurarlo y administrarlo a tu manera, y un bajo costo por un servicio de alta calidad y capacidad empresarial.'
    ]
  },
  {
    title: 'SEGURIDAD',
    image: `${IMG}/caracteristicas/gears.png`,
    imageAlt: 'Seguridad',
    paragraphs: [
      'Nuestro Data Center está equipado con equipos de seguridad, gestión de alimentación eléctrica, climatización y acceso restringido con cámara de seguridad. Todos nuestros servidores poseen redundancia de alimentación eléctrica y grupo electrógeno de respaldo.'
    ]
  },
  {
    title: 'ELECTRICIDAD REDUNDANTE',
    image: `${IMG}/caracteristicas/mail.png`,
    imageAlt: 'Electricidad',
    paragraphs: [
      'Un servidor debe tener suministro constante de energía y garantía de que no sufra subidas y bajadas bruscas. Contamos con UPS entre los servidores y la red eléctrica, y generadores que se activan si falla el suministro.'
    ]
  }
];

const SD_ADDON_TITLES_WITH_PRICE = new Set<string>([
  'LICENCIA WHM / CPANEL',
  'LICENCIA SOFTACULOUS'
]);

/** Plantilla de adicionales (precios desde JSON). */
const ADDON_TEMPLATE: Array<
  Omit<RackeoVirtualizacionAddOn, 'price' | 'priceDecimals'>
> = [
  {
    title: 'LICENCIA WHM / CPANEL',
    image: `${IMG}/addons/addon-cpanel.jpg`,
    imageAlt: 'LICENCIA WHM / CPANEL',
    priceNote: 'PAGO MENSUAL',
    description:
      'WHM es el panel líder en el mercado: te permitirá administrar tu sitio y también a tus clientes.',
    benefits: [
      'Posee una interfaz limpia',
      'Es fácil de utilizar',
      'Puedes obtener informes y estadísticas de tu sitio web',
      'Es completamente seguro'
    ],
    paymentHint: 'PAGO MENSUAL'
  },
  {
    title: 'UPGRADE DISCO DURO',
    image: `${IMG}/addons/addon-disco.jpg`,
    imageAlt: 'UPGRADE DISCO DURO',
    priceNote: 'PAGO MENSUAL',
    description:
      'Expande tu disco duro de acuerdo a tus necesidades pagando un bajo costo mensual y podrás utilizar la RAID con tarjeta controladora.',
    benefits: ['Personaliza tu Servidor', 'Puedes comprarlo si ya tienes un Servidor'],
    paymentHint: 'PAGO MENSUAL'
  },
  {
    title: '500 GB EXTRA TRANSFERENCIA MENSUAL',
    image: `${IMG}/addons/addon-transferencia.jpg`,
    imageAlt: '500 GB extra transferencia',
    priceNote: 'PAGO MENSUAL',
    description: 'Aumenta en rangos de 500 GB tu transferencia mensual para tu servidor dedicado.',
    benefits: ['Aumenta tu conectividad', 'Aumenta el rendimiento', 'Aumenta la velocidad'],
    paymentHint: 'PAGO MENSUAL'
  },
  {
    title: "IP's ADICIONALES",
    image: `${IMG}/addons/addon-ip.jpg`,
    imageAlt: "IP's adicionales",
    priceNote: 'PAGO MENSUAL',
    description:
      "Contrata tus 8 IP's adicionales para administrar tu cuenta como quieras.",
    benefits: ["Son IP's públicas adicionales", "Puedes contratar paquete de 8 IP's"],
    paymentHint: 'PAGO MENSUAL'
  },
  {
    title: 'RAM EXTRA',
    image: `${IMG}/addons/addon-ram.jpg`,
    imageAlt: 'RAM extra',
    priceNote: 'PAGO MENSUAL',
    description: 'Paga por cada 2 GB extra. No te limites: agrega más RAM a tu Servidor Dedicado.',
    benefits: ['Puedes ejecutar más procesos a la vez', 'Mejora el rendimiento de tu sitio'],
    paymentHint: 'PAGO MENSUAL'
  },
  {
    title: 'LICENCIA SOFTACULOUS',
    image: `${IMG}/addons/addon-softaculous.jpg`,
    imageAlt: 'LICENCIA SOFTACULOUS',
    priceNote: 'PAGO MENSUAL',
    description:
      'Software con scripts auto instalables como blogs, foros y sistemas de gestión de contenido.',
    benefits: ['Posee más de 250 script', 'Fácil de instalar'],
    paymentHint: 'PAGO MENSUAL'
  }
];

const DEFAULT_SD_PLAN_PRICES: number[] = [49000, 69000, 99000, 149000];

const DEFAULT_PRICING_CL: RackeoServidoresDedicadosData = {
  planPrices: DEFAULT_SD_PLAN_PRICES,
  planPriceDecimals: 0,
  addonPricing: [
    { price: 10000 },
    { price: 10000 },
    { price: 2000 },
    { price: 4000 },
    { price: 5000 },
    { price: 1000 }
  ],
  supportFirstPrices: [5900, 9900, 15900, 15900],
  supportExtraPrices: [2900, 5900, 9900, 9900],
  supportPriceDecimals: 0
};

export interface SdPlanView extends RackeoServidorDedicadoPlanSpec {
  monthlyPrice: number;
  priceDecimals: number;
}

@Component({
  selector: 'app-servidores-dedicados',
  templateUrl: './servidores-dedicados.component.html',
  styleUrls: ['./servidores-dedicados.component.scss'],
  animations: [sdPlanPanelExpand]
})
export class ServidoresDedicadosComponent implements OnInit {
  activeTab = 0;
  readonly tabPills = ['Generales', 'Caracteristicas', 'Soporte', 'Adicionales'];
  readonly tabSectionTitles = ['GENERAL', 'CARACTERISTICAS', 'SOPORTE', 'ADICIONALES'];
  readonly supportColumnLabels = ['PLAN1 NIVEL', 'PLAN2 NIVEL', 'PLAN3 NIVEL', 'PLAN4 NIVEL'];

  plans: SdPlanView[] = [];
  /** Panel de características abierto por id de plan (botón +). */
  expandedPlanIds: Record<string, boolean> = {};
  heroSrc = SD_HERO_DEFAULT_SRC;
  heroAlt = SD_HERO_DEFAULT_ALT;
  generalesIntroImage = `${IMG}/generales/sd_generales_left_image.png`;
  generalesBlocks = GENERALES_BLOCKS;
  caracteristicasBlocks = CARACTERISTICAS_BLOCKS;
  supportRows = SUPPORT_ROWS;
  addOns: RackeoVirtualizacionAddOn[] = [];

  supportFirstPrices: number[] = [...(DEFAULT_PRICING_CL.supportFirstPrices ?? [])];
  supportExtraPrices: number[] = [...(DEFAULT_PRICING_CL.supportExtraPrices ?? [])];
  supportPriceDecimals = 0;

  constructor(
    private readonly locale: RackeoLocaleService,
    public readonly cart: RackeoCartService,
    private readonly meta: MetaService,
    private readonly canonical: CanonicalService
  ) {}

  ngOnInit(): void {
    const country = RACKEO_SITE.countryName;
    this.meta.setTitle(`Servidores Dedicados ${country} | Setup Gratis | Rackeo`);
    this.meta.setTag('description', `Servidores Dedicados personalizables con reemplazo de hardware en 4 horas, data center Tier II en ${country} y soporte 24/7 por ticket, chat y teléfono.`);
    this.meta.setTag('og:type', 'website');
    this.meta.setTag('og:image:type', 'image/png');
    this.meta.setTag('og:image', RACKEO_SITE.ogImageUrl);
    this.meta.setTag('og:image:width', '1200');
    this.meta.setTag('og:image:height', '1200');
    this.meta.setTag('og:image:alt', `Servidores Dedicados ${country} | Setup Gratis | Rackeo`);
    this.meta.setTag('og:locale', RACKEO_SITE.ogLocale);
    this.canonical.set('/servidores-dedicados');
    this.locale.getContent().pipe(take(1)).subscribe((c) => {
      if (c) {
        this.applyContent(c);
      } else {
        this.applyPricing(DEFAULT_PRICING_CL);
      }
    });
  }

  private applyContent(content: RackeoContent): void {
    const sd = content.servidoresDedicados;
    const merged: RackeoServidoresDedicadosData = { ...DEFAULT_PRICING_CL, ...sd };
    const hi = sd?.heroImage?.trim();
    const ha = sd?.heroAlt?.trim();
    this.heroSrc = hi || SD_HERO_DEFAULT_SRC;
    this.heroAlt = ha || SD_HERO_DEFAULT_ALT;
    this.applyPricing(merged);
  }

  private applyPricing(sd: RackeoServidoresDedicadosData): void {
    const planDecimals = sd.planPriceDecimals ?? 0;
    const p = sd.planPrices?.length === 4 ? sd.planPrices : DEFAULT_SD_PLAN_PRICES;
    this.plans = PLAN_SPECS.map((spec, i) => ({
      ...spec,
      monthlyPrice: p[i],
      priceDecimals: planDecimals
    }));

    const apDef = DEFAULT_PRICING_CL.addonPricing ?? [];
    const apIn = sd.addonPricing;
    const ap =
      apIn?.length === 6
        ? apIn
        : apDef.map((d, i) => ({ ...d, ...apIn?.[i] }));
    this.addOns = ADDON_TEMPLATE.map((t, i) => {
      const pr = ap[i];
      const price = pr?.price ?? apDef?.[i]?.price ?? 0;
      const priceDecimals = pr?.priceDecimals ?? apDef?.[i]?.priceDecimals ?? 0;
      return { ...t, price, priceDecimals };
    });

    this.supportFirstPrices = [...(sd.supportFirstPrices ?? DEFAULT_PRICING_CL.supportFirstPrices ?? [])];
    this.supportExtraPrices = [...(sd.supportExtraPrices ?? DEFAULT_PRICING_CL.supportExtraPrices ?? [])];
    this.supportPriceDecimals = sd.supportPriceDecimals ?? 0;
  }

  setTab(i: number): void {
    if (i >= 0 && i < this.tabPills.length) {
      this.activeTab = i;
    }
  }

  togglePlanDetails(planId: string): void {
    this.expandedPlanIds = {
      ...this.expandedPlanIds,
      [planId]: !this.expandedPlanIds[planId]
    };
  }

  isPlanDetailsOpen(planId: string): boolean {
    return !!this.expandedPlanIds[planId];
  }

  ariaTogglePlan(plan: SdPlanView): string {
    return this.isPlanDetailsOpen(plan.id)
      ? `Ocultar características de ${plan.name}`
      : `Ver características de ${plan.name}`;
  }

  showAddonPrice(addon: RackeoVirtualizacionAddOn): boolean {
    const t = addon.title.trim();
    return SD_ADDON_TITLES_WITH_PRICE.has(t);
  }

  isTick(c: RackeoSdSupportCell): boolean {
    return c === 'tick';
  }

  isCross(c: RackeoSdSupportCell): boolean {
    return c === 'cross';
  }

  scrollToComoComprar(event: Event): void {
    event.preventDefault();
    if (typeof document === 'undefined') {
      return;
    }
    const el = document.getElementById('sd_como_comprar');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    }
  }

  hardwareImageForModel(model: string): string {
    const m = model.trim().toUpperCase();
    if (m.includes('2950')) {
      return `${IMG}/hardware/dell-poweredge-2950-front.png`;
    }
    return `${IMG}/hardware/dell-poweredge-1950-front.png`;
  }

  hardwareAltForModel(model: string): string {
    const m = model.trim().toUpperCase();
    if (m.includes('2950')) {
      return 'Dell PowerEdge 2950 — vista frontal';
    }
    return 'Dell PowerEdge 1950 — vista frontal';
  }
}
